import React from "react";

import { useParams } from "react-router-dom";
import { fetchEmployee } from "modules/Retention/services/retention";
import endpoints from "modules/Retention/constants/endpoints";
import { useQuery } from "@tanstack/react-query";
import RETENTION_ROUTES, { getEmployeeProfileRoute } from "modules/Retention/constants/routes";
import { BreadcrumbItem, IconName } from "modules/Retention/types";
import { Breadcrumb, EmployeeHeader } from "../../EmployeeProfile/components";
import { ExitSurvey } from "../../EscalationWithAI/components/ExitSurvey";
import { getEmployeeExitSurvey } from "modules/Retention/services/retention";
import { ExitSurveyAPIResponse } from "modules/Retention/types/exitSurvey";
import { ExitSurveyLog } from "./components/ExitSurveyLog";
import useUser from "hooks/userHooks";

export default function ExitSurveyDetailPage() {
    const {id} = useParams();
    const { user } = useUser();
    const employeeId = Number(id);

    const { data, isLoading: isLoadingEmployee, error: employeeError } = useQuery({
        queryKey: [endpoints.employee, employeeId],
        queryFn: () => fetchEmployee({ employee_id: employeeId }),
        select: (res) => res?.result?.data,
        enabled: !!employeeId,
    });

    const { data: exitSurveyData, isLoading: isLoadingExitSurvey, error: exitSurveyError } = useQuery({
        queryKey: [endpoints.employeeExitSurvey, employeeId],
        queryFn: () => getEmployeeExitSurvey({employee_id: employeeId}),
        enabled: !!employeeId,
        select: (res: any) => res?.result as ExitSurveyAPIResponse,
    }); 

    const breadcrumbItems: BreadcrumbItem[] = [
        { icon: IconName.FEATHER_HOME, label: "Homepage", href: RETENTION_ROUTES.HOME },
        { icon: IconName.FEATHER_LIST_CHECKS, label: "Exit Survey", href: RETENTION_ROUTES.EXIT_SURVEY },
        { icon: IconName.FEATHER_USER, label: `${data?.profile?.first_name || ''} ${data?.profile?.last_name || ''}`, href: getEmployeeProfileRoute(user?.agency_id).replace(":id", employeeId.toString()) },
        { icon: IconName.FEATHER_FILE_TEXT, label: "Exit Survey" },
    ];

    // Show error state if any critical query fails
    if (employeeError || exitSurveyError) {
        return (
            <div className="flex h-full w-full flex-col items-center justify-center bg-default-background">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Data</h2>
                    <p className="text-gray-600">
                        {employeeError ? "Failed to load employee information." : ""}
                        {exitSurveyError ? "Failed to load exit survey details." : ""}
                    </p>
                </div>
            </div>
        );
    }

    // Show loading state while fetching critical data
    if (isLoadingEmployee || isLoadingExitSurvey) {
        return (
            <div className="flex h-full w-full flex-col items-start bg-default-background pb-12">
                {/* Breadcrumb Skeleton */}
                <div className="flex h-16 w-full items-center justify-between border-b border-solid border-neutral-border px-4 py-3 md:px-8 lg:px-12 mb-4">
                    <div className="flex items-center gap-2">
                        {[1, 2, 3, 4].map((item) => (
                            <div key={item} className="flex items-center gap-2">
                                <div className="h-4 w-4 bg-neutral-200 rounded animate-pulse" />
                                <div className="h-4 w-20 bg-neutral-200 rounded animate-pulse" />
                                {item < 4 && <div className="h-4 w-4 bg-neutral-200 rounded animate-pulse" />}
                            </div>
                        ))}
                    </div>
                </div>
                
                <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-y-auto">
                    <div className="flex w-full flex-col items-start gap-4 px-4 pb-8 md:gap-6 md:px-8 lg:max-w-[1280px] lg:px-12">
                        {/* Employee Header Skeleton */}
                        <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background px-4 py-4">
                            <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-full bg-neutral-200 animate-pulse" />
                                    <div className="flex flex-col gap-1">
                                        <div className="h-5 w-32 bg-neutral-200 rounded animate-pulse" />
                                        <div className="h-4 w-24 bg-neutral-200 rounded animate-pulse" />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="h-8 w-20 bg-neutral-200 rounded-full animate-pulse" />
                                    <div className="h-8 w-24 bg-neutral-200 rounded-full animate-pulse" />
                                </div>
                            </div>
                        </div>

                        {/* Exit Survey Section Skeleton */}
                        <div className="flex w-full flex-col items-start gap-4 rounded-lg bg-error-50 px-4 py-4">
                            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="h-6 w-48 bg-neutral-200 rounded animate-pulse" />
                                <div className="h-6 w-24 bg-neutral-200 rounded-full animate-pulse" />
                            </div>
                            <div className="flex flex-col items-start gap-1">
                                <div className="h-4 w-32 bg-neutral-200 rounded animate-pulse" />
                            </div>
                        </div>

                        {/* Survey Details Skeleton */}
                        <div className="flex w-full flex-col items-start justify-end gap-1 rounded-lg border border-solid border-neutral-border bg-default-background">
                            <div className="flex w-full items-center justify-between rounded-lg px-4 py-2">
                                <div className="h-5 w-32 bg-neutral-200 rounded animate-pulse" />
                                <div className="h-4 w-4 bg-neutral-200 rounded animate-pulse" />
                            </div>
                            <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-2 border-t border-solid border-neutral-border bg-neutral-50 px-4 py-4">
                                <div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:justify-between">
                                    <div className="flex flex-col items-start gap-2">
                                        <div className="h-6 w-48 bg-neutral-200 rounded animate-pulse" />
                                    </div>
                                </div>
                                <div className="flex w-full flex-col items-start gap-1 rounded-md bg-white px-2 py-2 shadow-sm">
                                    {[1, 2, 3].map((item) => (
                                        <div key={item} className="flex w-full flex-col items-start border-b border-solid border-neutral-border px-1 py-1 last:border-b-0">
                                            <div className="w-full flex flex-col gap-2 justify-between sm:flex-row sm:items-start">
                                                <div className="w-full flex-1 h-4 bg-neutral-200 rounded animate-pulse" />
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <div className="h-6 w-20 bg-neutral-200 rounded animate-pulse" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Exit Survey Log Section Skeleton */}
                        <div className="flex w-full flex-col items-start gap-4">
                            <div className="h-6 w-32 bg-neutral-200 rounded animate-pulse" />
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                                <div className="h-10 w-32 bg-neutral-200 rounded animate-pulse" />
                                <div className="h-10 w-40 bg-neutral-200 rounded animate-pulse" />
                            </div>
                            
                            {/* Feed Timeline Skeleton */}
                            <div className="flex w-full flex-col items-start gap-4 px-2 md:max-w-[768px] md:px-6">
                                <div className="flex w-full flex-col items-start">
                                    {[1, 2, 3].map((item) => (
                                        <div key={item} className="flex w-full items-start gap-3 md:gap-4">
                                            <div className="flex flex-col items-center self-stretch">
                                                <div className="flex flex-col items-start gap-1">
                                                    <div className="h-8 w-8 rounded-full bg-neutral-200 animate-pulse" />
                                                </div>
                                                {item < 3 && (
                                                    <div className="flex w-0.5 grow shrink-0 basis-0 flex-col items-start gap-2 bg-neutral-border" />
                                                )}
                                            </div>
                                            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 pt-1 pb-7">
                                                <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                                                    <div className="flex grow shrink-0 basis-0 flex-wrap items-start gap-1">
                                                        <div className="h-4 w-32 bg-neutral-200 rounded animate-pulse" />
                                                        <div className="h-4 w-48 bg-neutral-200 rounded animate-pulse" />
                                                    </div>
                                                    <div className="h-4 w-24 bg-neutral-200 rounded animate-pulse" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col items-start bg-default-background pb-12">
            <Breadcrumb
                isLoading={isLoadingEmployee || !data?.profile?.first_name}
                items={breadcrumbItems}
            />
            <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-y-auto">
                <div className="flex w-full flex-col items-start gap-4 px-4 pb-8 md:gap-6 md:px-8 lg:max-w-[1280px] lg:px-12">
                    <EmployeeHeader
                        employeeId={employeeId}
                    />
                    <ExitSurvey data={exitSurveyData} />
                    <ExitSurveyLog data={exitSurveyData!} />
                </div>
            </div>
            
           
        </div>
    );
}